import { useState } from "react";
import {
  Box, Stack, Typography, IconButton, Tooltip,
  Menu, MenuItem, ListItemIcon, ListItemText
} from "@mui/material";

import CreateRoundedIcon from "@mui/icons-material/CreateRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

import IconPickerDialog from "./IconPickerDialog";
import ImageCropperDialog from "./ImageCropperDialog";
import IconRuntime from "./IconRuntime";

/**
 * Props:
 *  value: { imageUrl?: string|null, iconId?: string|null, color?: string }
 *  onChange: (next) => void
 *  noFrame?: boolean           // tar bort ram runt preview
 *  compact?: boolean           // mindre knappar/spacing
 *  hideHint?: boolean          // göm tipstexten
 *  height?: number             // höjd på preview (default 96)
 *  width?: number              // bredd på preview (default 96)
 */
export default function WorkspaceAvatarPicker({
  value,
  onChange,
  noFrame,
  compact,
  hideHint,
  height = 96,
  width = 96,
}) {
  const [iconOpen, setIconOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const hasImage = !!value?.imageUrl;
  const hasIcon = !!value?.iconId;
  const iconColor = value?.color || "#6a2135";

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setRawImage(reader.result); setCropOpen(true); };
    reader.readAsDataURL(file);
  };

  // Ikonstorlek anpassas till previewns storlek (med en liten marginal)
  const iconSize = Math.max(20, Math.min(width, height) - 12);

  return (
    // Viktigt: overflow: visible så att penna kan “hänga” utanför hörnet
    <Stack spacing={compact ? 0.75 : 1.25} alignItems="flex-start" sx={{ overflow: "visible" }}>
      {/* Wrapper tillåter overlay att placera sig utanför */}
      <Box sx={{ position: "relative", overflow: "visible", lineHeight: 0 }}>
        {/* Preview – storlek styrs av props */}
        <Box
          sx={{
            width,
            height,
            borderRadius: 2,
            bgcolor: "background.default",
            ...(noFrame ? {} : { border: "1px solid", borderColor: "divider" }),
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
          }}
          onClick={openMenu}
          role="button"
          aria-label="Redigera avatar"
        >
          {hasImage ? (
            <img
              src={value.imageUrl}
              alt="workspace"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (hasIcon ? (
            <IconRuntime icon={value.iconId} size={44} color={value?.color || "#6a2135"} />
          ) : (
            // Fallback-ikon när inget är valt
            <IconRuntime icon="material-symbols:workspaces" size={44} color={value?.color || "#6a2135"} />
          ))}
        </Box>

        {/* Penna utanför nedre högra hörnet med låg opacitet (blir starkare på hover) */}
        <Tooltip title="Redigera">
          <IconButton
            size="small"
            onClick={openMenu}
            aria-label="Redigera"
            sx={{
              position: "absolute",
              right: -8,                 // utanför
              bottom: -8,                // utanför
              backgroundColor: "rgba(255,255,255,0.75)",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: 1,
              opacity: 0.7,
              transition: "opacity 160ms ease, transform 160ms ease, background-color 160ms ease",
              "&:hover": {
                opacity: 1,
                backgroundColor: "rgba(255,255,255,0.9)",
                transform: "translateY(-1px)",
              },
            }}
          >
            <CreateRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {!hideHint && (
        <Typography variant="caption" color="text.secondary">
          Bild prioriteras. Saknas bild visas vald ikon i vald färg.
        </Typography>
      )}

      {/* Meny */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem component="label">
          <ListItemIcon><PhotoCameraRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Ladda upp bild" />
          <input type="file" hidden accept="image/*" onChange={(e) => { closeMenu(); onFile(e); }} />
        </MenuItem>
        <MenuItem onClick={() => { closeMenu(); setIconOpen(true); }}>
          <ListItemIcon><CategoryRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Välj ikon & färg" />
        </MenuItem>
        {(hasImage || hasIcon) && (
          <MenuItem onClick={() => { onChange({ ...value, imageUrl: null, iconId: null }); closeMenu(); }}>
            <ListItemIcon><DeleteOutlineRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Rensa" />
          </MenuItem>
        )}
      </Menu>

      {/* Dialoger */}
      <IconPickerDialog
        open={iconOpen}
        onClose={() => setIconOpen(false)}
        onPick={(payload) => {
          // payload kan vara en sträng (id) eller ett objekt { iconId?, icon?, id?, color? }
          const id =
            typeof payload === "string"
              ? payload
              : payload?.iconId || payload?.icon || payload?.id || null;

          const nextColor =
            typeof payload === "string"
              ? (value?.color || "#6a2135")
              : (payload?.color || value?.color || "#6a2135");

          // Sätt vald ikon och rensa ev. bild
          onChange({ ...value, iconId: id, color: nextColor, imageUrl: null });
        }}
      />
      <ImageCropperDialog
        open={cropOpen}
        src={rawImage}
        onClose={() => setCropOpen(false)}
        onDone={(imageUrl) => onChange({ ...value, imageUrl })}
      />
    </Stack>
  );
}
